import { usePage } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import useRouter from '@/hooks/useRouter';
import { Avatar, Flex, Text, DropdownMenu } from '@radix-ui/themes';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react';

import {
  CrossIcon,
  ExitIcon,
  HamburgerMenuIcon,
  UserIcon,
  GearIcon,
} from '@/components/icons';
import Button from '@/components/Button';
import EnvironmentBadge from '@/components/EnvironmentBadge';
import Feedback from '@/components/Feedback';
import FirmNavbar from '@/components/FirmNavbar';
import Sidebar from '@/components/Sidebar';

export default function SidebarLayout({ children }) {
  const { t } = useTranslation();
  const { route, router } = useRouter();
  const { url, props } = usePage();
  const { current_user, firm } = props;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = current_user.admin;
  const isAdminNamespace = url.startsWith('/admin');
  const isUserRoute = url.startsWith('/user/');
  const showFeedback = isUserRoute && isAdmin ? false : !isAdminNamespace;

  return (
    <div className="h-full bg-gray-50">
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <CrossIcon
                    aria-hidden="true"
                    className="h-6 w-6 text-white"
                  />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <Sidebar setSidebarOpen={setSidebarOpen} />
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col transition-opacity duration-300 ease-linear">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
          <Sidebar />
        </div>
      </div>

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <HamburgerMenuIcon aria-hidden="true" className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <Flex align="center" className="relative flex-1">
              {isAdminNamespace && (
                <Text weight="bold">{t('common.admin')}</Text>
              )}
              {!isAdminNamespace && firm && <FirmNavbar firm={firm} />}
              {isUserRoute && <Text weight="bold">{t('common.account')}</Text>}
            </Flex>

            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {showFeedback && <Feedback />}

              <EnvironmentBadge />

              <DropdownMenu.Root modal={false}>
                <DropdownMenu.Trigger>
                  <Button
                    size="3"
                    variant="ghost"
                    radius="full"
                    className="text-neutral-800 bg-transparent hover:bg-neutral-50 font-semibold text-[15px]"
                  >
                    <Avatar size="2" radius="full" fallback={<UserIcon />} />
                    <Text className="hidden xl:inline">
                      {current_user.name}
                    </Text>
                    <DropdownMenu.TriggerIcon className="text-neutral-500" />
                  </Button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content>
                  <DropdownMenu.Item
                    onSelect={() => router.visit('/user/account')}
                  >
                    <Flex align="center" gap="2">
                      <GearIcon size={16} weight="fill" />
                      {t('common.manage_account')}
                    </Flex>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => window.location.assign(route('/logout'))}
                  >
                    <ExitIcon size={16} />
                    {t('common.sign_out')}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
          </div>
        </div>
        <main className="pb-10">{children}</main>
      </div>
    </div>
  );
}
